import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api, ENTHAELT_NAME, standAnzeige, type Einlesung, type Mandant, type MandantKurz, type Stand } from '../lib/api'
import { Stammdaten } from '../teile/Stammdaten'
import { Filterfeld } from '../teile/Filterfeld'

interface Dokumentzeile {
  id: string
  bezeichnung: string
  dateiname: string
  jahr: number
  monat: number | null
  quartal: number | null
  /** Was ausser der fuehrenden Art noch in derselben Datei steht. */
  enthaelt: string[]
  /** Falsch, wenn die Einordnung nur aus dem Titel stammt. */
  art_aus_inhalt: boolean
  hinweis: string | null
  /** Gesetzt, wenn eine spaetere Fassung desselben Zeitraums diese abgeloest hat. */
  ersetzt_durch: string | null
  stand: Stand
  einlesung?: Einlesung
  eingegangen_am: string
}

/* Rechenwerke, die nicht die handelsrechtliche Grundlage der Analyse sind.
   Sie werden nicht versteckt, sondern benannt: in der Liste als Marke, im
   Filter als ein Schalter. */
const NICHT_HGB: Record<string, string> = { steuerbilanz: 'Steuerbilanz', ifrs: 'IFRS' }

function fremdesRechenwerk(dokument: Dokumentzeile): string | null {
  // Enthaelt ein Dokument die Handelsbilanz mit, ist es der Erstellungs-
  // bericht und das andere eine Anlage darin.
  if (dokument.enthaelt.includes('handelsbilanz')) return null
  for (const [schluessel, name] of Object.entries(NICHT_HGB)) {
    if (dokument.enthaelt.includes(schluessel)) return name
  }
  return null
}

/**
 * Was der Filter durchlaesst.
 *
 * Zwei Schalter, weil zwei verschiedene Fragen dahinterstehen: "welches
 * Rechenwerk" (Handelsbilanz gegen Steuerbilanz) und "welcher Stand"
 * (gueltige Fassung gegen abgeloeste). Beide Voreinstellungen zeigen das,
 * womit gearbeitet wird — alles andere ist ausdruecklich zuzuschalten.
 */
interface Filter {
  nurGueltige: boolean
  nurHandelsrecht: boolean
}

function passt(dokument: Dokumentzeile, filter: Filter): boolean {
  if (filter.nurGueltige && dokument.ersetzt_durch) return false
  if (filter.nurHandelsrecht && fremdesRechenwerk(dokument)) return false
  return true
}

const spalten = { gridTemplateColumns: 'minmax(0, 1fr) 110px 130px' }

const SPALTE_SPEICHER = 'mandantenspalte-offen'

function useSpalteOffen() {
  const [offen, setzeOffen] = useState(() => {
    try {
      return localStorage.getItem(SPALTE_SPEICHER) !== 'zu'
    } catch {
      return true
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(SPALTE_SPEICHER, offen ? 'offen' : 'zu')
    } catch {
      /* Ohne Speicher klappt die Spalte trotzdem, sie merkt es sich nur nicht. */
    }
  }, [offen])

  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault()
        setzeOffen((vorher) => !vorher)
      }
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [])

  return [offen, setzeOffen] as const
}

export function MandantSeite() {
  const { werkzeug, id } = useParams()
  const [suche, setzeSuche] = useState('')
  const [spalteOffen, setzeSpalteOffen] = useSpalteOffen()
  const [filter, setzeFilter] = useState<Filter>({ nurGueltige: true, nurHandelsrecht: true })

  const mandant = useQuery({
    queryKey: ['mandant', id],
    queryFn: () => api.holen<{ mandant: Mandant }>(`/api/mandanten/${id}`),
    enabled: Boolean(id),
  })

  const dokumente = useQuery({
    queryKey: ['dokumente', werkzeug, id],
    queryFn: () =>
      api.holen<{ dokumente: Dokumentzeile[] }>(`/api/dokumente?werkzeug=${werkzeug}&mandant=${id}`),
    enabled: Boolean(id),
  })

  // Die Liste links: alle Mandanten, damit man ohne Umweg zum nächsten kommt.
  const alle = useQuery({
    queryKey: ['mandanten', ''],
    queryFn: () => api.holen<{ mandanten: MandantKurz[] }>('/api/mandanten'),
  })

  const titel = werkzeug === 'bwa' ? 'BWA' : 'Jahresabschluss'

  if (mandant.isPending) return <div className="laedt">Wird geladen …</div>

  if (mandant.error) {
    return (
      <div className="liste">
        <div className="leer">
          <strong>Dieser Mandant konnte nicht geladen werden.</strong>
          <p>{mandant.error instanceof Error ? mandant.error.message : ''}</p>
        </div>
      </div>
    )
  }

  const m = mandant.data.mandant

  const alleZeilen = dokumente.data?.dokumente ?? []
  const sichtbar = alleZeilen.filter((d) => passt(d, filter))
  const abgeloest = alleZeilen.filter((d) => d.ersetzt_durch).length
  const fremde = alleZeilen.filter((d) => fremdesRechenwerk(d)).length

  const akte = (
    <>
      <div className="akte-kopf">
        <Stammdaten mandant={m} name={m.name} />
      </div>

      {/* Nur zeigen, was es zu filtern gibt. Bei einem Mandanten ohne
          abgeloeste Fassungen und ohne Steuerbilanz waere die Leiste eine
          Reihe wirkungsloser Schalter. */}
      {(abgeloest > 0 || fremde > 0) && (
        <div className="filterleiste">
          {/* Nur die Bruchzahl. "18 ausgeblendet" daneben ist 24 minus 6 —
              das rechnet der Leser selbst, und es ist die dritte Zahl in
              einer Zeile, die mit zwei auskommt. */}
          <span className="leiser">
            {sichtbar.length} von {alleZeilen.length}
          </span>

          {abgeloest > 0 && (
            <label className="schalter" title="Ältere Fassungen desselben Zeitraums ausblenden">
              <input
                type="checkbox"
                checked={filter.nurGueltige}
                onChange={(e) => setzeFilter({ ...filter, nurGueltige: e.target.checked })}
              />
              nur gültige Fassung
            </label>
          )}

          {fremde > 0 && (
            <label
              className="schalter"
              title="Steuerbilanz und IFRS sind eigene Rechenwerke. Die Analyse rechnet handelsrechtlich."
            >
              <input
                type="checkbox"
                checked={filter.nurHandelsrecht}
                onChange={(e) => setzeFilter({ ...filter, nurHandelsrecht: e.target.checked })}
              />
              nur Handelsrecht
            </label>
          )}
        </div>
      )}

      <div className="liste">
        <div className="listenkopf" style={spalten}>
          <div>{titel}</div>
          <div>Eingegangen</div>
          <div>Stand</div>
        </div>

        {dokumente.isPending && <div className="laedt">Wird geladen …</div>}

        {dokumente.data && dokumente.data.dokumente.length === 0 && (
          <div className="leer">
            <strong>Für diesen Mandanten liegt noch keine {titel} vor.</strong>
            <p>Sobald die Verbindung zu DATEV steht, erscheinen die Auswertungen hier.</p>
          </div>
        )}

        {dokumente.data && dokumente.data.dokumente.length > 0 && sichtbar.length === 0 && (
          <div className="leer">
            <strong>Der Filter blendet alle {alleZeilen.length} Einträge aus.</strong>
            <p>Schalte oben rechts einen der beiden Filter ab.</p>
          </div>
        )}

        {sichtbar.map((dokument) => (
          <Link
            key={dokument.id}
            className={`zeile${dokument.ersetzt_durch ? ' abgeloest' : ''}`}
            style={spalten}
            to={`/${werkzeug}/dokumente/${dokument.id}`}
            title="Öffnet die Auswertung. Der Zugriff wird protokolliert."
          >
            <div>
              <div className="sache">
                {dokument.bezeichnung}
                {fremdesRechenwerk(dokument) && (
                  <span className="marke stark">{fremdesRechenwerk(dokument)}</span>
                )}
                {dokument.ersetzt_durch && <span className="marke">abgelöst</span>}
              </div>
              <div className="nebensache">
                {dokument.dateiname}
                {/* Eine Paket-PDF enthaelt mehrere Auswertungen. Das gehoert
                    sichtbar dazu, sonst haelt man sie fuer eine reine BWA. */}
                {dokument.enthaelt.length > 1 &&
                  ` · enthält ${dokument.enthaelt.map((e) => ENTHAELT_NAME[e] ?? e).join(', ')}`}
              </div>
              {(!dokument.art_aus_inhalt || dokument.hinweis) && (
                <div className="nebensache warnung">
                  {dokument.hinweis ?? 'Einordnung stammt aus dem Titel, nicht aus dem Inhalt.'}
                </div>
              )}
            </div>
            <div className="leiser zahl">
              {new Date(dokument.eingegangen_am).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </div>
            {(() => {
              const anzeige = standAnzeige(dokument.stand, dokument.einlesung)
              return <div className={`stand ${anzeige}`}>{anzeige}</div>
            })()}
          </Link>
        ))}
      </div>
    </>
  )

  const gefiltert = (alle.data?.mandanten ?? []).filter((e) => {
    if (!suche.trim()) return true
    const s = suche.trim().toLowerCase()
    return e.name.toLowerCase().includes(s) || String(e.mandantennummer).startsWith(s)
  })

  return (
    <div className={`zweispaltig${spalteOffen ? '' : ' spalte-zu'}`}>
      <nav className="mandantenspalte" aria-label="Mandanten">
        {/* Sitzt auf der Trennlinie, nicht in der Spalte — sonst waere er
            weg, sobald die Spalte zu ist. */}
        <button
          type="button"
          className="spaltenschalter"
          onClick={() => setzeSpalteOffen(!spalteOffen)}
          aria-expanded={spalteOffen}
          aria-label={spalteOffen ? 'Mandantenliste ausblenden' : 'Mandantenliste einblenden'}
          title={`${spalteOffen ? 'Ausblenden' : 'Einblenden'} (\u2318\\)`}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d={spalteOffen ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
          </svg>
        </button>

        <Filterfeld wert={suche} setzeWert={setzeSuche} beschriftung="Mandanten filtern" />

        <div className="mandantenzeilen">
          {gefiltert.map((eintrag) => (
            <Link
              key={eintrag.id}
              to={`/${werkzeug}/mandanten/${eintrag.id}`}
              className={`mandantenzeile${eintrag.id === m.id ? ' aktiv' : ''}`}
            >
              <span className="sache">{eintrag.name}</span>
              <span className="nebensache zahl">{eintrag.mandantennummer}</span>
            </Link>
          ))}

          {alle.data && gefiltert.length === 0 && (
            <p className="leiser" style={{ padding: 'var(--space-sm)' }}>
              Kein Mandant passt zu dieser Suche.
            </p>
          )}
        </div>
      </nav>

      <div className="akte">{akte}</div>
    </div>
  )
}
